import type { CSSProperties } from "react";
import { useOneShotTime, useReducedMotion } from "./cinematic/clock";
import { SceneWin } from "./cinematic/SceneWin";
import { SceneBust } from "./cinematic/SceneBust";
import {
  playKeyClick,
  playLockCrack,
  playDoorSwing,
  playWinSting,
  playBustThud,
} from "../lib/sound.js";

/**
 * Vault Breaker — Chest Opening.
 *
 * Wrapper around the cinematic vault scenes. Routes by `outcome` to either
 * `SceneWin` (gold cascade, door swings TOWARD camera) or `SceneBust` (ash
 * dissolve, door swings AWAY). Drives wall-clock time via `useOneShotTime`
 * and bridges audio cues from the scenes to the procedural Web Audio module.
 *
 * Public contract (preserved for `EndScreens` callers):
 *   - Props:    { multiplier, outcome?, duration?, onComplete? }
 *   - onComplete: fired once at `duration` seconds (or sooner if reduced
 *                 motion shortens the timeline).
 *   - Reduced motion: shows a static peak frame for ~2s before completing.
 *   - Fits the EndModal frame (4:3, max ~720x540).
 */

interface ChestOpeningProps {
  multiplier: number;
  outcome?: "win" | "bust";
  duration?: number;
  /** Player's stake in their currency (drives the BTC amount in the result banner). */
  stake?: number;
  /** Currency code shown in the result banner. */
  currency?: string;
  onComplete?: () => void;
}

// Authored canvas — the EndModal sizes the frame; scenes scale their internal
// composition to fill via `position: absolute; inset: 0`.
const W = 720;
const H = 520;
const V = 420;

export function ChestOpening({
  multiplier,
  outcome = "win",
  duration,
  stake = 0.001,
  currency = "BTC",
  onComplete,
}: ChestOpeningProps) {
  const reduce = useReducedMotion();
  const dur = duration ?? (outcome === "bust" ? 4.0 : 7.2);
  const effectiveDur = reduce ? 2.0 : dur;
  const t = useOneShotTime(effectiveDur, onComplete);

  // Audio dispatcher — maps scene cues to the procedural cue functions in
  // lib/sound.ts. Each cue is fired exactly once per scene mount (the scene
  // tracks fired-cues internally).
  const handleAudioCue = (cue: string) => {
    if (cue === "cashoutFlash" || cue === "keyStrike") playKeyClick();
    else if (cue === "lockCrack") playLockCrack();
    else if (cue === "doorSwing") playDoorSwing();
    else if (cue === "winSting") playWinSting();
    else if (cue === "bustThud") playBustThud();
  };

  const wrapperStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    zIndex: 50,
    overflow: "hidden",
  };

  return (
    <div style={wrapperStyle} data-palette={outcome}>
      {outcome === "bust" ? (
        <SceneBust
          multiplier={multiplier}
          t={t}
          duration={effectiveDur}
          width={W}
          height={H}
          vaultSize={V}
          stake={stake}
          currency={currency}
          onAudioCue={handleAudioCue}
        />
      ) : (
        <SceneWin
          multiplier={multiplier}
          t={t}
          duration={effectiveDur}
          width={W}
          height={H}
          vaultSize={V}
          stake={stake}
          currency={currency}
          onAudioCue={handleAudioCue}
        />
      )}
    </div>
  );
}
